import React, { useState } from 'react';
import { labelCls, inputCls } from './financialShared';
import { formatCurrencyBRL } from './financialUtils';
import { toCents, centsToInput, splitInstallments, totalFromInstallment } from './installmentCalc';

/**
 * Os três campos de valor de um lançamento parcelado — valor total, quantidade
 * de parcelas e valor da parcela — sempre sincronizados: editar qualquer um
 * recalcula os outros dois (o último campo tocado é a fonte da verdade).
 *
 * O estado que sobe para o modal continua sendo `amount` (TOTAL) e
 * `installments`: o payload da API não muda. Usado pelos dois modais gêmeos de
 * lançamento (conta corrente e fatura de cartão).
 */
interface InstallmentAmountFieldsProps {
  amount: string;
  onAmountChange: (value: string) => void;
  installments: string;
  onInstallmentsChange: (value: string) => void;
  /** false na edição: só o valor, sem parcelamento (comportamento atual). */
  showInstallments: boolean;
}

export const InstallmentAmountFields: React.FC<InstallmentAmountFieldsProps> = ({
  amount,
  onAmountChange,
  installments,
  onInstallmentsChange,
  showInstallments,
}) => {
  const count = Math.max(1, parseInt(installments, 10) || 1);
  const [installmentValue, setInstallmentValue] = useState<string>(() => {
    const cents = toCents(amount);
    return cents == null || cents === 0 ? '' : centsToInput(splitInstallments(cents, count).base);
  });
  const [lastEdited, setLastEdited] = useState<'total' | 'installment'>('total');

  const recomputeInstallment = (totalStr: string, n: number) => {
    const cents = toCents(totalStr);
    setInstallmentValue(cents == null || cents === 0 ? '' : centsToInput(splitInstallments(cents, n).base));
  };

  const handleTotal = (value: string) => {
    setLastEdited('total');
    onAmountChange(value);
    recomputeInstallment(value, count);
  };

  const handleCount = (value: string) => {
    onInstallmentsChange(value);
    const n = parseInt(value, 10);
    if (isNaN(n) || n < 1) return;
    if (lastEdited === 'installment') {
      // A parcela é a âncora: o total acompanha a quantidade nova.
      const per = toCents(installmentValue);
      if (per != null && per > 0) onAmountChange(centsToInput(totalFromInstallment(per, n)));
    } else {
      recomputeInstallment(amount, n);
    }
  };

  const handleInstallment = (value: string) => {
    setLastEdited('installment');
    setInstallmentValue(value);
    const per = toCents(value);
    if (per != null && per > 0) onAmountChange(centsToInput(totalFromInstallment(per, count)));
  };

  if (!showInstallments) {
    return (
      <div>
        <label className={labelCls}>Valor (R$) *</label>
        <input
          className={inputCls}
          type="number"
          min="0.01"
          step="0.01"
          value={amount}
          onChange={(e) => handleTotal(e.target.value)}
          placeholder="0,00"
        />
      </div>
    );
  }

  // O aviso do resto: quando o total não divide exato, a 1ª parcela leva os
  // centavos — mesma regra do backend e da Cielo.
  let remainderHint: string | null = null;
  if (lastEdited === 'total' && count > 1) {
    const cents = toCents(amount);
    if (cents != null && cents > 0) {
      const { base, first } = splitInstallments(cents, count);
      if (first !== base) {
        remainderHint = `1ª parcela: ${formatCurrencyBRL(first / 100)} (leva os centavos de resto)`;
      }
    }
  }

  return (
    <div className="space-y-1">
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>Valor total (R$) *</label>
          <input
            className={inputCls}
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(e) => handleTotal(e.target.value)}
            placeholder="0,00"
          />
        </div>
        <div>
          <label className={labelCls}>Parcelas</label>
          <input
            className={inputCls}
            type="number"
            min="1"
            step="1"
            value={installments}
            onChange={(e) => handleCount(e.target.value)}
          />
        </div>
        <div>
          <label className={labelCls}>Valor da parcela (R$)</label>
          <input
            className={inputCls}
            type="number"
            min="0.01"
            step="0.01"
            value={installmentValue}
            onChange={(e) => handleInstallment(e.target.value)}
            placeholder="0,00"
          />
        </div>
      </div>
      {remainderHint && <p className="text-xs text-slate-500">{remainderHint}</p>}
    </div>
  );
};
